import React, { useState } from 'react';
import { HelpCircle, Mail, Link as LinkIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { emailService } from '../services/emailService';

interface HelpButtonProps {
  currentModule?: string;
}

const HelpButton: React.FC<HelpButtonProps> = ({ currentModule }) => {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim()) return;
    
    setSending(true);
    
    try {
      // Construct detailed email body with user information
      const emailBody = `
        <h3>Hilfeanfrage von Benutzer</h3>
        <p><strong>Benutzerinformationen:</strong></p>
        <ul>
          <li><strong>Name:</strong> ${user?.name || 'Nicht verfügbar'}</li>
          <li><strong>E-Mail:</strong> ${user?.email || 'Nicht verfügbar'}</li>
          <li><strong>Unternehmen:</strong> ${user?.company || 'Nicht verfügbar'}</li>
          <li><strong>Benutzer-ID:</strong> ${user?.id || 'Nicht verfügbar'}</li>
          ${currentModule ? `<li><strong>Aktuelles Modul:</strong> ${currentModule}</li>` : ''}
        </ul>
        
        <p><strong>Nachricht:</strong></p>
        <p>${message.replace(/\n/g, '<br>')}</p>
        
        <hr>
        <p><em>Diese Nachricht wurde über das Hilfe-System der KI-Navigatoren Plattform gesendet.</em></p>
      `;

      const emailSubject = `Hilfeanfrage von ${user?.name || 'Unbekannter Benutzer'}${currentModule ? ` - Modul: ${currentModule}` : ''}`;

      await emailService.sendMail({
        to: 'aiko@ki-navigatoren.de',
        subject: emailSubject,
        body: emailBody
      });
      
      setSent(true);
      setMessage('');
    } catch (error) {
      console.error('Error sending help request:', error);
      alert('Fehler beim Senden. Bitte versuchen Sie es später erneut.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="fixed bottom-24 right-8 flex items-center gap-2 bg-[#2574B5] hover:bg-blue-700 text-white px-6 py-3 rounded-full shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl z-40"
      >
        <HelpCircle className="w-6 h-6" />
        <span className="font-medium">Hilfe</span>
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-xl font-semibold text-gray-800">
                Wie können wir Ihnen helfen?
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {sent ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 className="text-lg font-medium text-gray-800 mb-2">
                  Anfrage gesendet!
                </h4>
                <p className="text-gray-600 mb-6">
                  Wir melden uns schnellstmöglich bei Ihnen.
                </p>
                <button
                  onClick={() => setShowModal(false)}
                  className="bg-gray-100 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-200"
                >
                  Schließen
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <Link 
                    to="/faq"
                    className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                  >
                    <LinkIcon className="w-6 h-6 text-[#2574B5]" />
                    <div>
                      <h4 className="font-medium text-gray-800">FAQ</h4>
                      <p className="text-sm text-gray-600">Häufig gestellte Fragen</p>
                    </div>
                  </Link>

                  <a 
                    href="mailto:info@ki-navigatoren.de"
                    className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                  >
                    <Mail className="w-6 h-6 text-[#2574B5]" />
                    <div>
                      <h4 className="font-medium text-gray-800">E-Mail</h4>
                      <p className="text-sm text-gray-600">info@ki-navigatoren.de</p>
                    </div>
                  </a>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Ihre Nachricht
                    </label>
                    <textarea
                      id="message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Beschreiben Sie Ihr Anliegen..."
                    ></textarea>
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="submit"
                      disabled={sending || !message.trim()}
                      className={`px-4 py-2 text-white bg-[#2574B5] rounded-md transition-colors ${
                        sending || !message.trim() 
                          ? 'opacity-50 cursor-not-allowed' 
                          : 'hover:bg-blue-700'
                      }`}
                    >
                      {sending ? 'Wird gesendet...' : 'Absenden'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default HelpButton;